import { OAuthServiceType } from "@common/constants";
import { UnifiedConnectionProvider } from "@common/schemas/organization-unified-connection.schema";

/**
 * How a given provider's reconnect is driven.
 *
 * - `plaid` — Link opens in update mode; connection-scoped, no workspace needed.
 * - `oauthPopup` — a popup posts an auth code back, then `target` picks which fix
 *   endpoint receives it (`native` is the workspace-scoped one).
 * - `paypal` — its own SDK handshake yielding `authCode` + `sharedId`; PayPal has
 *   no `OAuthServiceType`, so it cannot share the popup path.
 * - `unsupported` — no reconnect flow exists anywhere in the app.
 */
export type OrgReconnectStrategy =
    | { kind: "plaid" }
    | {
          kind: "oauthPopup";
          scope: OAuthServiceType;
          target: "stripe" | "ramp" | "native";
      }
    | { kind: "paypal" }
    | { kind: "unsupported" };

/**
 * Exhaustive by construction: a provider added to `unifiedConnectionProviderSchema`
 * fails to compile here rather than silently defaulting to unsupported.
 */
const STRATEGY_BY_PROVIDER: Record<
    UnifiedConnectionProvider,
    OrgReconnectStrategy
> = {
    plaid: { kind: "plaid" },
    stripe: {
        kind: "oauthPopup",
        scope: OAuthServiceType.STRIPE,
        target: "stripe",
    },
    ramp: { kind: "oauthPopup", scope: OAuthServiceType.RAMP, target: "ramp" },
    quickbooks: {
        kind: "oauthPopup",
        scope: OAuthServiceType.QUICKBOOKS,
        target: "native",
    },
    mercury: {
        kind: "oauthPopup",
        scope: OAuthServiceType.MERCURY,
        target: "native",
    },
    gusto: {
        kind: "oauthPopup",
        scope: OAuthServiceType.GUSTO,
        target: "native",
    },
    adp_run: {
        kind: "oauthPopup",
        scope: OAuthServiceType.ADP_RUN,
        target: "native",
    },
    paypal: { kind: "paypal" },
    billcom: { kind: "unsupported" },
};

export const orgReconnectStrategy = (
    provider: UnifiedConnectionProvider,
): OrgReconnectStrategy => STRATEGY_BY_PROVIDER[provider];

/** Every distinct popup scope, so the provider can mount one listener each. */
export const ORG_RECONNECT_OAUTH_SCOPES: OAuthServiceType[] = [
    ...new Set(
        Object.values(STRATEGY_BY_PROVIDER).flatMap((strategy) =>
            strategy.kind === "oauthPopup" ? [strategy.scope] : [],
        ),
    ),
];
